import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useFirebase } from '../contexts/FirebaseContext'
import { ArrowLeft, Save, Eye, Check } from 'lucide-react'
import toast from 'react-hot-toast'

const QuestionAdd = () => {
  const [formData, setFormData] = useState({
    question: '',
    option1: '',
    option2: '',
    option3: '',
    option4: '',
    correctAnswer: '',
    category: '',
    difficulty: 'medium',
    explanation: ''
  })
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(false)
  const [showPreview, setShowPreview] = useState(false)
  
  const { addQuestion, getCategories } = useFirebase()
  const navigate = useNavigate()

  useEffect(() => {
    loadCategories()
  }, [])

  const loadCategories = async () => {
    try {
      const categoriesData = await getCategories()
      setCategories(categoriesData)
    } catch (error) {
      console.error('Error loading categories:', error)
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!formData.question.trim()) {
      toast.error('Please enter a question')
      return
    }
    
    if (!formData.option1.trim() || !formData.option2.trim() || 
        !formData.option3.trim() || !formData.option4.trim()) {
      toast.error('Please fill in all four options')
      return
    }
    
    if (formData.correctAnswer === '') {
      toast.error('Please select the correct answer')
      return
    }

    setLoading(true)
    
    try {
      const questionData = {
        question: formData.question.trim(),
        options: [
          formData.option1.trim(),
          formData.option2.trim(),
          formData.option3.trim(),
          formData.option4.trim()
        ],
        correctAnswer: parseInt(formData.correctAnswer),
        category: formData.category || '',
        difficulty: formData.difficulty,
        explanation: formData.explanation.trim()
      }
      
      await addQuestion(questionData)
      toast.success('Question added successfully')
      navigate('/questions')
    } catch (error) {
      console.error('Error adding question:', error)
      toast.error('Failed to add question')
    } finally {
      setLoading(false)
    }
  }

  const isPreviewReady = formData.question && formData.option1 && formData.option2 && 
                        formData.option3 && formData.option4

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Add New Question</h1>
          <p className="text-sm text-gray-600">
            Create a new exam question for your nursing app
          </p>
        </div>
        <Link to="/questions" className="btn-outline">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Questions
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Form */}
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium text-gray-900">Question Details</h3>
          </div>
          
          <div className="card-body">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Question Text */}
              <div>
                <label htmlFor="question" className="form-label">
                  Question Text *
                </label>
                <textarea
                  id="question"
                  name="question"
                  rows={4}
                  className="form-textarea"
                  placeholder="Enter the question text..."
                  value={formData.question}
                  onChange={handleChange}
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Write a clear and concise question
                </p>
              </div>

              {/* Options */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="option1" className="form-label">
                    Option A *
                  </label>
                  <input
                    type="text"
                    id="option1"
                    name="option1"
                    className="form-input"
                    placeholder="Enter option A"
                    value={formData.option1}
                    onChange={handleChange}
                    required
                  />
                </div>
                
                <div>
                  <label htmlFor="option2" className="form-label">
                    Option B *
                  </label>
                  <input
                    type="text"
                    id="option2"
                    name="option2"
                    className="form-input"
                    placeholder="Enter option B"
                    value={formData.option2}
                    onChange={handleChange}
                    required
                  />
                </div>
                
                <div>
                  <label htmlFor="option3" className="form-label">
                    Option C *
                  </label>
                  <input
                    type="text"
                    id="option3"
                    name="option3"
                    className="form-input"
                    placeholder="Enter option C"
                    value={formData.option3}
                    onChange={handleChange}
                    required
                  />
                </div>
                
                <div>
                  <label htmlFor="option4" className="form-label">
                    Option D *
                  </label>
                  <input
                    type="text"
                    id="option4"
                    name="option4"
                    className="form-input"
                    placeholder="Enter option D"
                    value={formData.option4}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              {/* Correct Answer */}
              <div>
                <label htmlFor="correctAnswer" className="form-label">
                  Correct Answer *
                </label>
                <select
                  id="correctAnswer"
                  name="correctAnswer"
                  className="form-select"
                  value={formData.correctAnswer}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select the correct answer</option>
                  <option value="0">Option A</option>
                  <option value="1">Option B</option>
                  <option value="2">Option C</option>
                  <option value="3">Option D</option>
                </select>
              </div>

              {/* Category and Difficulty */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="category" className="form-label">
                    Category
                  </label>
                  <select
                    id="category"
                    name="category"
                    className="form-select"
                    value={formData.category}
                    onChange={handleChange}
                  >
                    <option value="">Select a category</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    <Link to="/categories/add" className="text-primary-600 hover:text-primary-800">
                      Create new category
                    </Link>
                  </p>
                </div>
                
                <div>
                  <label htmlFor="difficulty" className="form-label">
                    Difficulty Level
                  </label>
                  <select
                    id="difficulty"
                    name="difficulty"
                    className="form-select"
                    value={formData.difficulty}
                    onChange={handleChange}
                  >
                    <option value="easy">Easy</option>
                    <option value="medium">Medium</option>
                    <option value="hard">Hard</option>
                  </select>
                </div>
              </div>

              {/* Explanation */}
              <div>
                <label htmlFor="explanation" className="form-label">
                  Explanation (Optional)
                </label>
                <textarea
                  id="explanation"
                  name="explanation"
                  rows={3}
                  className="form-textarea"
                  placeholder="Provide an explanation for the correct answer..."
                  value={formData.explanation}
                  onChange={handleChange}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Help students understand why this is the correct answer
                </p>
              </div>

              {/* Submit Buttons */}
              <div className="flex space-x-4 pt-6 border-t border-gray-200">
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary"
                >
                  {loading ? (
                    <div className="flex items-center">
                      <div className="loading-spinner h-4 w-4 mr-2"></div>
                      Saving...
                    </div>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Save Question
                    </>
                  )}
                </button>
                <Link to="/questions" className="btn-outline">
                  Cancel
                </Link>
              </div>
            </form>
          </div>
        </div>

        {/* Preview */}
        <div className="card">
          <div className="card-header">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">
                Question Preview
              </h3>
              <button
                onClick={() => setShowPreview(!showPreview)}
                className="btn-outline text-sm"
              >
                <Eye className="h-4 w-4 mr-1" />
                {showPreview ? 'Hide' : 'Show'} Preview
              </button>
            </div>
          </div>
          
          <div className="card-body">
            {isPreviewReady && showPreview ? (
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">
                    {formData.question}
                  </h4>
                  <div className="space-y-2">
                    {['A', 'B', 'C', 'D'].map((label, index) => {
                      const option = formData[`option${index + 1}`]
                      const isCorrect = parseInt(formData.correctAnswer) === index
                      
                      return (
                        <div
                          key={label}
                          className={`flex items-center space-x-3 p-3 rounded-md ${
                            isCorrect 
                              ? 'bg-success-50 border border-success-200' 
                              : 'bg-gray-50'
                          }`}
                        >
                          <span className="font-medium text-gray-700">
                            {label}.
                          </span>
                          <span className="text-gray-900">{option}</span>
                          {isCorrect && (
                            <Check className="h-4 w-4 text-success-600 ml-auto" />
                          )}
                        </div>
                      )
                    })}
                  </div>
                  
                  {formData.explanation && (
                    <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                      <h5 className="font-medium text-blue-900 mb-1">
                        Explanation:
                      </h5>
                      <p className="text-blue-800 text-sm">
                        {formData.explanation}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <Eye className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">
                  {isPreviewReady 
                    ? 'Click "Show Preview" to see how your question will look'
                    : 'Fill in the question and options to see a preview'
                  }
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default QuestionAdd
